import {
  FC,
  useState,
  useEffect,
  createContext,
  useContext,
  useRef,
  Dispatch,
  SetStateAction,
} from 'react'
import {LayoutSplashScreen} from '../../../../_metronic/layout/core'
import {AuthModel, UserModel} from './_models'
import * as authHelper from './AuthHelpers'
import {
  checkAuthToken,
  getUserByToken,
  setUserApplicationId,
  setUserDealerId,
  setUserLanguageId,
} from './_requests'
import {WithChildren} from '../../../../_metronic/helpers'

type AuthContextProps = {
  auth: AuthModel | undefined
  saveAuth: (auth: AuthModel | undefined) => void
  currentUser: UserModel | undefined
  setCurrentUser: Dispatch<SetStateAction<UserModel | undefined>>
  logout: () => void
  setSelectedLanguage: (id: string | number, withReload?: boolean) => void
  setSelectedDealer: (id: string | number, withReload?: boolean) => void
  setSelectedApplication: (id: string | number, withReload?: boolean) => void
  requireSettingsSelection: () => boolean
  refreshUser: () => void
}

const initAuthContextPropsState = {
  auth: authHelper.getAuth(),
  saveAuth: () => {},
  currentUser: undefined,
  setCurrentUser: () => {},
  logout: () => {},
  setSelectedLanguage: (id: string | number) => {},
  setSelectedDealer: (id: string | number) => {},
  setSelectedApplication: (id: string | number) => {},
  requireSettingsSelection: () => true,
  refreshUser: () => {},
}

const AuthContext = createContext<AuthContextProps>(initAuthContextPropsState)

const useAuth = () => {
  return useContext(AuthContext)
}

const AuthProvider: FC<WithChildren> = ({children}) => {
  const [auth, setAuth] = useState<AuthModel | undefined>(authHelper.getAuth())
  const [currentUser, setCurrentUser] = useState<UserModel | undefined>()
  const saveAuth = (auth: AuthModel | undefined) => {
    setAuth(auth)
    if (auth) {
      authHelper.setAuth(auth)
    } else {
      authHelper.removeAuth()
    }
  }

  const logout = () => {
    saveAuth(undefined)
    setCurrentUser(undefined)
  }

  const setSelectedLanguage = async (id: string | number, withReload: boolean = false) => {
    await setUserLanguageId(id)
    if (withReload) reloadPage()
  }

  const setSelectedApplication = async (id: string | number, withReload: boolean = false) => {
    await setUserApplicationId(id)
    if (withReload) reloadPage()
  }

  const setSelectedDealer = async (id: string | number, withReload: boolean = false) => {
    await setUserDealerId(id)
    if (withReload) reloadPage()
  }

  const reloadPage = () => {
    window.location.reload()
  }

  const requireSettingsSelection = (): boolean => {
    const languageSelected = currentUser?.language != null
    const dealerSelected = currentUser?.dealer_id != null
    const applicationSelected = currentUser?.application_id != null

    return !languageSelected || !dealerSelected || !applicationSelected
  }

  const refreshUser = async () => {
    const checkResult = await checkAuthToken()
    const {data: user} = await getUserByToken()
    setCurrentUser(user)
  }

  return (
    <AuthContext.Provider
      value={{
        auth,
        saveAuth,
        currentUser,
        setCurrentUser,
        logout,
        setSelectedApplication,
        setSelectedDealer,
        setSelectedLanguage,
        requireSettingsSelection,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

const AuthInit: FC<WithChildren> = ({children}) => {
  const {auth, saveAuth, logout, setCurrentUser} = useAuth()
  const didRequest = useRef(false)
  const [showSplashScreen, setShowSplashScreen] = useState(true)
  // We should request user by authToken (IN OUR EXAMPLE IT'S API_TOKEN) before rendering the application
  useEffect(() => {
    const requestUser = async () => {
      try {
        if (!didRequest.current) {
          const {data: checkAuthResult} = await checkAuthToken()
          if (checkAuthResult.new_token != null) {
            saveAuth({
              jwt_key: checkAuthResult.jwt_key,
              token: checkAuthResult.new_token!,
            })
          }
          const {data} = await getUserByToken()
          if (data) {
            setCurrentUser(data)
          }
        }
      } catch (error) {
        console.error(error)
        if (!didRequest.current) {
          logout()
        }
      } finally {
        setShowSplashScreen(false)
      }

      return () => (didRequest.current = true)
    }

    if (auth != null) {
      requestUser()
    } else {
      logout()
      setShowSplashScreen(false)
    }
    // eslint-disable-next-line
  }, [])

  return showSplashScreen ? <LayoutSplashScreen /> : <>{children}</>
}

export { AuthProvider, AuthInit, useAuth };
